import {
  Bookmark, Send, PhoneCall, FileSearch, Star, Users, Trophy, XCircle,
} from 'lucide-react';

/**
 * Job Tracker — shared constants.
 *
 * Colours are drawn from the palette already used across the MarsResume
 * dashboard (sidebar nav, interview cards) so the board reads as native.
 * Brand red stays reserved for primary actions and the active state.
 */

export const STAGES = [
  // Saved but not sent yet — the only stage before an application exists.
  { id: 'bookmarked',  label: 'Bookmarked',  icon: Bookmark,   color: '#14B8A6', bg: '#CCFBF1' },
  { id: 'applied',     label: 'Applied',     icon: Send,       color: '#0EA5E9', bg: '#E0F2FE' },
  { id: 'screening',   label: 'Screening',   icon: PhoneCall,  color: '#6366F1', bg: '#EEF2FF' },
  { id: 'in_review',   label: 'In Review',   icon: FileSearch, color: '#8B5CF6', bg: '#F5F3FF' },
  { id: 'shortlisted', label: 'Shortlisted', icon: Star,       color: '#F59E0B', bg: '#FEF3C7' },
  { id: 'interview',   label: 'Interview',   icon: Users,      color: '#EC4899', bg: '#FCE7F3' },
  { id: 'offer',       label: 'Offer',       icon: Trophy,     color: '#10B981', bg: '#D1FAE5' },
  { id: 'rejected',    label: 'Rejected',    icon: XCircle,    color: '#94A3B8', bg: '#F1F5F9' },
];

export const STAGE_IDS = STAGES.map(s => s.id);

export const stageMeta = (id) => STAGES.find(s => s.id === id) || STAGES[0];

export const JOB_TYPES = [
  { value: '',           label: 'Not specified' },
  { value: 'full-time',  label: 'Full-time' },
  { value: 'part-time',  label: 'Part-time' },
  { value: 'contract',   label: 'Contract' },
  { value: 'internship', label: 'Internship' },
  { value: 'temporary',  label: 'Temporary' },
  { value: 'remote',     label: 'Remote' },
];

export const DATE_FILTERS = [
  { value: 'all',   label: 'Any date' },
  { value: '7',     label: 'Last 7 days' },
  { value: '30',    label: 'Last 30 days' },
  { value: '90',    label: 'Last 3 months' },
];

/* Design tokens lifted from the existing dashboard so the module stays
   visually consistent without importing from unrelated files. */
export const T = {
  brand:       '#EF1D28',
  brandDark:   '#C5161D',
  brandGrad:   'linear-gradient(135deg,#EF1D28,#FF5C5C)',
  brandSoft:   '#FFF1F2',
  brandBorder: '#FECDD3',
  ink:         '#111827',
  body:        '#374151',
  muted:       '#6B7280',
  faint:       '#9CA3AF',
  line:        '#E5E7EB',
  lineSoft:    '#EAECF0',
  surface:     '#FFFFFF',
  canvas:      '#F8FAFC',
  slate:       '#F1F5F9',
  radius:      12,
  radiusLg:    16,
  font:        "'Inter', sans-serif",
  shadowCard:  '0 1px 6px rgba(0,0,0,0.06)',
  shadowHover: '0 10px 28px rgba(15,23,42,0.10)',
  shadowBrand: '0 4px 16px rgba(239,29,40,0.35)',
};

/** "Aug 8" / "Aug 8, 2025" when the year differs from now. */
export const fmtDate = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString('en-US',
    sameYear ? { month: 'short', day: 'numeric' } : { month: 'short', day: 'numeric', year: 'numeric' });
};

/** yyyy-mm-dd for <input type="date"> */
export const toDateInput = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  const tzAdjusted = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return tzAdjusted.toISOString().slice(0, 10);
};

/** Deterministic accent per company so avatars stay stable between renders. */
const AVATAR_COLORS = ['#0EA5E9', '#6366F1', '#8B5CF6', '#EC4899', '#F59E0B', '#10B981', '#EF4444', '#14B8A6'];
export const companyAccent = (company = '') => {
  let hash = 0;
  for (let i = 0; i < company.length; i++) hash = (hash * 31 + company.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

export const companyInitials = (company = '') => company
  .split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';

/** Only allow links we are willing to open in a new tab. */
export const safeExternalUrl = (raw = '') => {
  const url = String(raw).trim();
  if (!url) return '';
  const withProto = /^https?:\/\//i.test(url) ? url : `https://${url}`;
  try {
    const parsed = new URL(withProto);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? parsed.href : '';
  } catch { return ''; }
};
